import type { ReactNode } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import TextLink from 'LuxKit/TextLink'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import OfferPropertyFlexibleCancellationPolicyTimelineModal from './OfferPropertyFlexibleCancellationPolicyTimelineModal'
import { defineMessages, useIntl } from 'react-intl'
import Colour from 'components/utils/Colour'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'
import type { FlexibleCancellationTimelineDetails } from './flexibleCancellationPolicyTimelineUtils'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

const FLEXIBLE_CANCELLATION_TOOLTIP_ICON = <MiddledTooltipIcon size="XS" />

const cancellationMessages = defineMessages({
  dateHotel: {
    id: 'xklLzofgKlFbwimlm1wq',
    defaultMessage: '<bold>Flexible cancellation</bold> until {date} (Hotel only) {tooltipIcon}',
    description: 'Flexible hotel cancellation policy shown with the applicable booking coverage and tooltip',
  },
  dateStandalone: {
    id: '2rEaCSWqhn0Dt8kGn2qs',
    defaultMessage: '<bold>Flexible cancellation</bold> until {date} {tooltipIcon}',
    description: 'Flexible hotel cancellation policy shown with the applicable booking coverage and tooltip',
  },
  purchaseHotel: {
    id: 'MHEFU8DDYhz_f_Dauryb',
    defaultMessage: '<bold>Flexible cancellation</bold> up to 7 days after purchase (Hotel only) {tooltipIcon}',
    description: 'Flexible hotel cancellation policy shown with the applicable booking coverage and tooltip',
  },
  purchaseStandalone: {
    id: 'IW_vixbvDtxnLnfI1fwr',
    defaultMessage: '<bold>Flexible cancellation</bold> up to 7 days after purchase {tooltipIcon}',
    description: 'Flexible hotel cancellation policy shown with the applicable booking coverage and tooltip',
  },
  genericHotel: {
    id: 'C3FRh9pSroa6PajLyscW',
    defaultMessage: '<bold>Flexible cancellation</bold> (Hotel only) {tooltipIcon}',
    description: 'Flexible hotel cancellation policy shown with the applicable booking coverage and tooltip',
  },
  genericStandalone: {
    id: 'nEqVQMN1Nr62rbWcrAts',
    defaultMessage: '<bold>Flexible cancellation</bold> {tooltipIcon}',
    description: 'Flexible hotel cancellation policy shown with the applicable booking coverage and tooltip',
  },
})

interface Props {
  details: FlexibleCancellationTimelineDetails
  isCharter: boolean
  isFlashUSP?: boolean
  showHotelOnly?: boolean
}

/**
 * Presentational flexible-cancellation USP/link. No Redux or eligibility derivation —
 * callers pass a resolved timeline context (or fields from one).
 */
function OfferPropertyFlexibleCancellationPoliciesView(props: Props) {
  const { details, isCharter, isFlashUSP, showHotelOnly = false } = props
  const intl = useIntl()
  const uspColour = useRebrandSuccessDarkColour()
  const intlDateFormatter = useIntlDateFormatter()
  const showModal = useContext(ModalContext)

  const {
    startBasedOnCheckIn,
    checkInDate: detailsCheckInDate,
  } = details

  const onItemClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((e) => {
    e.preventDefault()
    showModal(
      <OfferPropertyFlexibleCancellationPolicyTimelineModal
        details={details}
        isCharter={isCharter}
      />,
    )
  }, [details, isCharter, showModal])

  const formatFlexibleCancellationBold = useCallback((chunks: React.ReactNode) => (
    <Colour key="flexible-cancellation-bold" value={uspColour} as="b">{chunks}</Colour>
  ), [uspColour])

  const richTextValues = useMemo(() => ({
    bold: formatFlexibleCancellationBold,
    tooltipIcon: FLEXIBLE_CANCELLATION_TOOLTIP_ICON,
  }), [formatFlexibleCancellationBold])

  let cancellationPolicyText: ReactNode = null

  if (detailsCheckInDate && startBasedOnCheckIn) {
    cancellationPolicyText = intl.formatMessage(
      showHotelOnly ? cancellationMessages.dateHotel : cancellationMessages.dateStandalone,
      {
        ...richTextValues,
        date: intlDateFormatter(startBasedOnCheckIn, INTL_SHORT_DAY_NAME_DAY_MONTH),
      },
    )
  } else if (!detailsCheckInDate) {
    cancellationPolicyText = intl.formatMessage(
      showHotelOnly ? cancellationMessages.purchaseHotel : cancellationMessages.purchaseStandalone,
      richTextValues,
    )
  } else {
    cancellationPolicyText = intl.formatMessage(
      showHotelOnly ? cancellationMessages.genericHotel : cancellationMessages.genericStandalone,
      richTextValues,
    )
  }

  const flexibleCancellationLinkLabel = showHotelOnly ? intl.formatMessage({
    id: 'e04ET6-UfLHACpQDyEOB',
    defaultMessage: 'Flexible cancellation (Hotel only)',
    description: 'Cancellation link clarifying hotel-only coverage when another travel product is selected',
  }) : intl.formatMessage({
    id: 'TPCgs5T7ix7LMSAopVJI',
    defaultMessage: 'Flexible cancellation',
    description: 'Link text for flexible cancellation policy in the cancellation flow',
  })

  return <>
    {!isFlashUSP &&
      <Clickable onClick={onItemClick}>
        <BodyText variant="medium" as="span">
          {cancellationPolicyText}
        </BodyText>
      </Clickable>}
    {isFlashUSP &&
      <TextLink
        size="medium"
        colour={uspColour}
        underline={false}
        data-type="7day"
        onClick={onItemClick}
        startIcon={<LineRefundIcon />}
      >
        {flexibleCancellationLinkLabel}
      </TextLink>}
  </>
}

export default OfferPropertyFlexibleCancellationPoliciesView
